import { useCallback, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { TextSettings, TextStrokePosition } from '../types';
import { ColorSwatchPopover } from './ColorSwatchPopover';
import { GoogleFontsPicker } from './GoogleFontsPicker';

const SECTION_CLASS = 'space-y-3 flex-1 min-w-[240px] max-w-sm';

const STROKE_POSITIONS: { id: TextStrokePosition; label: string }[] = [
  { id: 'outside', label: 'Outside' },
  { id: 'center', label: 'Center' },
  { id: 'inside', label: 'Inside' },
];

interface TextPanelProps {
  settings: TextSettings;
  onChange: (s: Partial<TextSettings>) => void;
  recentColors?: string[];
  onRecentColorPicked?: (color: string) => void;
  /** When true the active layer is a text bound to a vector path —
   *  the Arch slider grays out (path overrides arch). See doc on
   *  PropertiesPanelProps.isActiveTextBoundToPath. */
  isActiveTextBoundToPath?: boolean;
}

/** Text-tool properties panel.
 *  Tier 2 Group B brings: Google Fonts picker (replaces native
 *  dropdown), tracking + leading sliders, Stroke accordion
 *  (width / color / position), Drop Shadow accordion (offset / blur
 *  / color). Bind / unbind row for text-on-path lives at the TOP
 *  of PropertiesPanel (same slot as boolean-ops), not here, so
 *  it stays visible across tools. */
export function TextPanel({
  settings,
  onChange,
  recentColors,
  onRecentColorPicked,
  isActiveTextBoundToPath,
}: TextPanelProps) {
  const archDisabled = !!isActiveTextBoundToPath;
  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Text</h4>

      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Font</span>
        <GoogleFontsPicker
          value={settings.fontFamily}
          onChange={(value) => onChange({ fontFamily: value })}
        />
      </label>

      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Size</span>
        <input
          type="number"
          min={8}
          max={200}
          value={settings.fontSize}
          onChange={(e) => onChange({ fontSize: Number(e.target.value) })}
          className="flex-1 text-xs bg-background border border-border rounded px-2 py-1"
        />
      </label>

      <label className="flex items-start gap-2">
        <span className="text-xs w-14 pt-2">Color</span>
        <ColorSwatchPopover
          value={{ type: 'solid', color: settings.fill }}
          onChange={(v) => v.type === 'solid' && onChange({ fill: v.color })}
          allowGradient={false}
          recentColors={recentColors}
          onRecentColorPicked={onRecentColorPicked}
          label="Text color"
        />
      </label>

      <div className="flex gap-1">
        <button
          type="button"
          className={`px-2 py-1 text-xs rounded ${settings.fontWeight === 'bold' ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
          onClick={() =>
            onChange({ fontWeight: settings.fontWeight === 'bold' ? 'normal' : 'bold' })
          }
          aria-pressed={settings.fontWeight === 'bold'}
        >
          B
        </button>
        <button
          type="button"
          className={`px-2 py-1 text-xs rounded italic ${settings.fontStyle === 'italic' ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
          onClick={() =>
            onChange({ fontStyle: settings.fontStyle === 'italic' ? 'normal' : 'italic' })
          }
          aria-pressed={settings.fontStyle === 'italic'}
        >
          I
        </button>
        <button
          type="button"
          className={`px-2 py-1 text-xs rounded underline ${settings.underline ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
          onClick={() => onChange({ underline: !settings.underline })}
          aria-pressed={settings.underline}
        >
          U
        </button>
      </div>

      <SliderRow
        label="Tracking"
        title="Letter spacing. 0 = font default. Positive opens text up; negative tightens."
        min={-200}
        max={800}
        step={10}
        value={settings.letterSpacing}
        onChange={(v) => onChange({ letterSpacing: v })}
      />

      <SliderRow
        label="Leading"
        title="Line height. Multiplier of font size. 1 = packed; 1.16 = default; 2 = double-spaced. Only visible on multi-line text."
        min={0.8}
        max={3}
        step={0.05}
        value={settings.lineHeight}
        onChange={(v) => onChange({ lineHeight: v })}
        format={(v) => v.toFixed(2)}
      />

      <label className="flex items-center gap-2">
        <span className={`text-xs w-14 ${archDisabled ? 'text-muted-foreground' : ''}`}>Arch</span>
        <input
          type="range"
          min={-100}
          max={100}
          value={settings.arch}
          onChange={(e) => onChange({ arch: Number(e.target.value) })}
          disabled={archDisabled}
          className="flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
          title={
            archDisabled
              ? 'Arch is disabled while the active text is bound to a path. Unbind to re-enable.'
              : undefined
          }
        />
        <span className="text-xs w-8 text-right tabular-nums">{settings.arch}</span>
      </label>

      <StrokeAccordion
        stroke={settings.stroke}
        onChange={(stroke) => onChange({ stroke })}
        recentColors={recentColors}
        onRecentColorPicked={onRecentColorPicked}
      />

      <DropShadowAccordion
        shadow={settings.dropShadow}
        onChange={(dropShadow) => onChange({ dropShadow })}
        recentColors={recentColors}
        onRecentColorPicked={onRecentColorPicked}
      />

      {/* Text-on-path bind / unbind UI lives at the TOP of the
          PropertiesPanel body — same slot as the boolean-ops row.
          Surfacing it there (rather than inside this tool-gated
          TextPanel) means the user discovers it without first
          switching to the Text tool, and it stays visible when
          multi-selecting from any other tool's panel. The arch
          slider above still respects the binding via
          `archDisabled`. */}
    </div>
  );
}

interface SliderRowProps {
  label: string;
  title?: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
  /** Custom formatter for the readout. Default: integer. */
  format?: (v: number) => string;
}

function SliderRow({ label, title, min, max, step, value, onChange, format }: SliderRowProps) {
  const formatted = format ? format(value) : String(Math.round(value));
  return (
    <label className="flex items-center gap-2" title={title}>
      <span className="text-xs w-14">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1"
      />
      <span className="text-xs w-10 text-right tabular-nums">{formatted}</span>
    </label>
  );
}

interface AccordionProps {
  title: string;
  enabled: boolean;
  /** Initial expanded state. Header click overrides. */
  defaultOpen?: boolean;
  /** Toggle render in the header — drives the `enabled` boolean
   *  the caller persists. Clicking the title doesn't toggle this. */
  onToggle: (next: boolean) => void;
  children: React.ReactNode;
}

function Accordion({ title, enabled, defaultOpen = false, onToggle, children }: AccordionProps) {
  const [open, setOpen] = useState(defaultOpen || enabled);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="border-t border-border pt-2">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase hover:text-foreground"
          aria-expanded={open}
        >
          <Chevron className="h-3 w-3" />
          {title}
        </button>
        <label className="inline-flex items-center gap-1 text-[11px] text-muted-foreground select-none">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onToggle(e.target.checked)}
            className="h-3 w-3"
          />
          <span>{enabled ? 'On' : 'Off'}</span>
        </label>
      </div>
      {open ? <div className="mt-2 space-y-2">{children}</div> : null}
    </div>
  );
}

interface StrokeAccordionProps {
  stroke: TextSettings['stroke'];
  onChange: (stroke: TextSettings['stroke']) => void;
  recentColors?: string[];
  onRecentColorPicked?: (color: string) => void;
}

const DEFAULT_STROKE: NonNullable<TextSettings['stroke']> = {
  width: 2,
  color: '#000000',
  position: 'outside',
};

function StrokeAccordion({
  stroke,
  onChange,
  recentColors,
  onRecentColorPicked,
}: StrokeAccordionProps) {
  const handleToggle = useCallback(
    (next: boolean) => onChange(next ? (stroke ?? DEFAULT_STROKE) : undefined),
    [stroke, onChange],
  );
  const handlePatch = useCallback(
    (patch: Partial<NonNullable<TextSettings['stroke']>>) =>
      onChange({ ...(stroke ?? DEFAULT_STROKE), ...patch }),
    [stroke, onChange],
  );
  return (
    <Accordion title="Stroke" enabled={!!stroke} onToggle={handleToggle}>
      <SliderRow
        label="Width"
        min={0}
        max={20}
        step={0.5}
        value={stroke?.width ?? DEFAULT_STROKE.width}
        onChange={(v) => handlePatch({ width: v })}
        format={(v) => v.toFixed(1)}
      />
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Color</span>
        <ColorSwatchPopover
          value={{ type: 'solid', color: stroke?.color ?? DEFAULT_STROKE.color }}
          onChange={(v) => v.type === 'solid' && handlePatch({ color: v.color })}
          allowGradient={false}
          recentColors={recentColors}
          onRecentColorPicked={onRecentColorPicked}
          label="Stroke color"
        />
      </label>
      <div className="flex items-center gap-2">
        <span className="text-xs w-14">Position</span>
        <div className="flex gap-1">
          {STROKE_POSITIONS.map((p) => {
            const isActive = (stroke?.position ?? DEFAULT_STROKE.position) === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => handlePatch({ position: p.id })}
                className={`px-2 py-1 text-[11px] rounded ${
                  isActive ? 'bg-primary text-primary-foreground' : 'bg-accent'
                }`}
                aria-pressed={isActive}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>
    </Accordion>
  );
}

interface DropShadowAccordionProps {
  shadow: TextSettings['dropShadow'];
  onChange: (shadow: TextSettings['dropShadow']) => void;
  recentColors?: string[];
  onRecentColorPicked?: (color: string) => void;
}

const DEFAULT_SHADOW: NonNullable<TextSettings['dropShadow']> = {
  color: '#000000',
  offsetX: 3,
  offsetY: 3,
  blur: 4,
};

function DropShadowAccordion({
  shadow,
  onChange,
  recentColors,
  onRecentColorPicked,
}: DropShadowAccordionProps) {
  const handleToggle = useCallback(
    (next: boolean) => onChange(next ? (shadow ?? DEFAULT_SHADOW) : undefined),
    [shadow, onChange],
  );
  const handlePatch = useCallback(
    (patch: Partial<NonNullable<TextSettings['dropShadow']>>) =>
      onChange({ ...(shadow ?? DEFAULT_SHADOW), ...patch }),
    [shadow, onChange],
  );
  return (
    <Accordion title="Drop Shadow" enabled={!!shadow} onToggle={handleToggle}>
      <SliderRow
        label="Offset X"
        min={-50}
        max={50}
        step={1}
        value={shadow?.offsetX ?? DEFAULT_SHADOW.offsetX}
        onChange={(v) => handlePatch({ offsetX: v })}
      />
      <SliderRow
        label="Offset Y"
        min={-50}
        max={50}
        step={1}
        value={shadow?.offsetY ?? DEFAULT_SHADOW.offsetY}
        onChange={(v) => handlePatch({ offsetY: v })}
      />
      <SliderRow
        label="Blur"
        min={0}
        max={50}
        step={1}
        value={shadow?.blur ?? DEFAULT_SHADOW.blur}
        onChange={(v) => handlePatch({ blur: v })}
      />
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Color</span>
        <ColorSwatchPopover
          value={{ type: 'solid', color: shadow?.color ?? DEFAULT_SHADOW.color }}
          onChange={(v) => v.type === 'solid' && handlePatch({ color: v.color })}
          allowGradient={false}
          recentColors={recentColors}
          onRecentColorPicked={onRecentColorPicked}
          label="Shadow color"
        />
      </label>
    </Accordion>
  );
}
